import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Button, Input } from "../components/common/Primitives";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { Shield, AlertTriangle } from "lucide-react";
import Seo from "../components/Seo";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@venturaz.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const blockedGoogle = location.state?.blockedGoogle;
  const blockedMessage = location.state?.message;

  useEffect(() => {
    if (blockedGoogle && blockedMessage) toast.warning(blockedMessage);
  }, [blockedGoogle, blockedMessage]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (!["super_admin", "admin", "manager", "agent"].includes(u.role)) {
        toast.error("This account is not an admin.");
        return;
      }
      toast.success("Welcome, " + u.name);
      nav("/admin/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-bg text-white flex items-center justify-center px-6" data-testid="admin-login-page">
      <Seo
        title="Admin Console | VenturaZ"
        description="Restricted access. VenturaZ admin console for platform operations."
        path="/admin/login"
        noindex
      />
      <Toaster richColors position="top-right" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="text-gold" size={18} />
          <div className="overline">VENTURAZ · ADMIN CONSOLE</div>
        </div>
        <h1 className="font-display text-3xl text-white mb-2">Restricted access.</h1>
        <p className="text-ink-muted text-sm mb-6">Super Admin, Manager and Agent accounts only.</p>
        {blockedGoogle && (
          <div className="flex items-start gap-3 border border-gold/40 bg-gold/[0.06] p-4 mb-5 rounded-md" data-testid="admin-blocked-google-banner">
            <AlertTriangle className="text-gold shrink-0 mt-0.5" size={16} />
            <div className="text-sm text-white/85">
              {blockedMessage || "Team accounts cannot use Google sign-in. Please enter your admin credentials below."}
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4 border border-white/10 bg-ink-surface p-6" data-testid="admin-login-form">
          <Input label="Admin Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testId="admin-email" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testId="admin-password" />
          <Button type="submit" disabled={loading} className="w-full" data-testid="admin-login-submit">
            {loading ? "Verifying…" : "Sign in to Console"}
          </Button>
          <div className="text-xs text-ink-faint text-center pt-2 space-x-3">
            <Link to="/forgot-password" className="hover:text-gold" data-testid="admin-login-forgot-link">Forgot password?</Link>
            <span className="text-ink-faint">·</span>
            <Link to="/" className="hover:text-gold">← Back to homepage</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
